import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { X, AlertTriangle, ExternalLink } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });
const mmss = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—";

// ProRoast palette (matches the Genio app)
const C_BT     = "#D72121";   // bean temp     = red
const C_ET     = "#1F54E0";   // env temp      = blue
const C_BT_ROR = "#E97B1B";   // BT rate       = orange
const C_ET_ROR = "#7B4FE0";   // ET rate       = violet (dashed)
const C_GAS    = "#D72121";   // gas line      = red (top mini)
const C_FAN    = "#1F54E0";   // fan           = blue (top mini)
const C_DRM    = "#E97B1B";   // drum          = orange (top mini)
const C_FC     = "#D946B5";   // first crack   = magenta

export default function RoastDetailModal({ roastId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!roastId) return;
    setData(null); setError("");
    api.get(`/roast-profiles/${roastId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load roast"));
  }, [roastId]);

  if (!roastId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/50 p-0 sm:p-4" data-testid="roast-detail-modal">
      <div className="bg-card border border-border rounded-t sm:rounded shadow-xl w-full max-w-6xl max-h-[96vh] sm:max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="min-w-0 flex-1">
            <p className="label-tiny">Roast detail</p>
            <h3 className="font-display text-base sm:text-xl font-bold leading-tight">
              {data ? (data.origin_name || "—") : "Loading…"}
            </h3>
            {data?.start_datetime && (
              <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
                {new Date(data.start_datetime).toLocaleString("en-ZA")}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="roast-detail-close" className="p-2 hover:bg-secondary rounded flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && <p className="p-4 sm:p-6 text-destructive">{error}</p>}

        {data && (
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Vital stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
              <Stat label="Green" value={`${fmt(data.green_weight_kg)} kg`} />
              <Stat label="Roasted" value={`${fmt(data.roasted_weight_kg)} kg`} />
              <Stat label="Loss" value={data.loss_pct != null ? `${fmt(data.loss_pct, 1)}%` : "—"}
                accent={data.loss_pct != null && (data.loss_pct < 8 || data.loss_pct > 22)} />
              <Stat label="Total time" value={mmss(data.roast_time_seconds)} />
              <Stat label="First crack" value={mmss(data.first_crack_time)} />
              <Stat label="Develop %" value={data.development_pct ? `${fmt(data.development_pct, 1)}%` : "—"} />
            </div>

            {(data.flags || []).length > 0 && (
              <div className="bg-card border border-[hsl(31,65%,53%)] rounded p-3 flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-[hsl(31,65%,53%)] mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold">Flagged for review</p>
                  <p className="text-muted-foreground">{data.flags.join(", ")}</p>
                </div>
                <button
                  data-testid="roast-clear-flags"
                  onClick={async () => {
                    if (!window.confirm("Clear all flags on this roast?")) return;
                    await api.patch(`/roast-profiles/${data.id}/flags`, { flags: [] });
                    setData({ ...data, flags: [] });
                  }}
                  className="px-3 py-1 text-xs border border-[hsl(31,65%,53%)] text-[hsl(31,65%,53%)] rounded hover:bg-[hsl(31,65%,53%)]/10 self-start"
                >
                  Clear flags
                </button>
              </div>
            )}

            {/* Curve — ProRoast-style layout */}
            <div className="bg-card border border-border rounded p-2 sm:p-4">
              <p className="label-tiny mb-2 sm:mb-3 px-2 sm:px-0">Roast curve · ProRoast-style</p>
              {data.curve && data.curve.length > 0 ? (
                <RoastCurve curve={data.curve} firstCrack={data.first_crack_time} />
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No curve data captured for this roast.
                </p>
              )}
            </div>

            {/* Notes */}
            {data.note && (
              <div data-testid="roast-note">
                <p className="label-tiny mb-1.5">ProRoast roast note</p>
                <p className="text-sm bg-secondary p-3 rounded whitespace-pre-wrap">{data.note}</p>
              </div>
            )}

            {/* Linked cuppings */}
            {(data.cuppings || []).length > 0 && (
              <div data-testid="roast-cuppings">
                <p className="label-tiny mb-1.5">Linked cuppings ({data.cuppings.length})</p>
                <div className="space-y-1.5">
                  {data.cuppings.map((c) => (
                    <Link
                      key={c.id}
                      to={`/cupping/${c.id}`}
                      onClick={onClose}
                      data-testid={`roast-cupping-${c.id}`}
                      className="flex items-center justify-between bg-secondary/40 hover:bg-secondary border border-border rounded px-3 py-2 text-sm group"
                    >
                      <span>
                        {c.session_number != null && (
                          <span className="font-mono text-xs px-1.5 py-0.5 mr-2 bg-primary/10 text-primary rounded font-bold">#{c.session_number}</span>
                        )}
                        <span className="font-mono text-xs text-muted-foreground mr-2">
                          {c.session_date ? new Date(c.session_date).toLocaleDateString("en-ZA") : ""}
                        </span>
                        <span className="font-medium">{c.title || "Cupping session"}</span>
                        {c.sample_labels?.length > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">— {c.sample_labels.join(", ")}</span>
                        )}
                      </span>
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`bg-card border rounded p-2 sm:p-3 ${accent ? "border-[hsl(31,65%,53%)]" : "border-border"}`}>
      <p className="label-tiny mb-1">{label}</p>
      <p className="font-display text-base sm:text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

// ProRoast-style multi-strip chart: thin gas/fan/drum strips on top, large
// BT+ET+RoR temperature chart below. Sharing one X-axis (time in seconds).
function MiniStrip({ data, dataKey, color, label, unit, domain, tMax }) {
  // Grab the last defined value so the operator can see the "current"
  // setpoint at a glance — hard to eyeball on a compressed strip.
  let latest = null;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i]?.[dataKey] != null) { latest = data[i][dataKey]; break; }
  }
  return (
    <div className="h-16 -mb-1">
      <div className="flex items-center gap-2 px-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground" style={{ minWidth: 32 }}>
          {label}
        </span>
        <span className="text-[10px]" style={{ color }}>● {unit}</span>
        {latest != null && (
          <span className="ml-auto text-[10px] font-mono tabular-nums font-semibold" style={{ color }}>
            {typeof latest === "number" ? latest.toFixed(0) : latest} {unit}
          </span>
        )}
      </div>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 0, right: 30, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(25 10% 65%)" strokeOpacity={0.5} vertical horizontal={false} />
          <XAxis dataKey="t" type="number" domain={tMax ? [0, tMax] : ["dataMin", "dataMax"]} hide />
          <YAxis width={36} tick={{ fontSize: 9, fill: "hsl(25 8% 42%)" }} domain={domain} />
          <Line dataKey={dataKey} stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function RoastCurve({ curve, firstCrack }) {
  // Compute end-time so the mini strips share the same X domain as the main chart.
  const tMax = curve.length ? curve[curve.length - 1].t : 0;
  return (
    <div>
      <MiniStrip data={curve} dataKey="gas" color={C_GAS} label="Gas" unit="%" domain={[0, 100]} tMax={tMax} />
      <MiniStrip data={curve} dataKey="fan" color={C_FAN} label="Fan" unit="Hz" domain={["dataMin - 2", "dataMax + 2"]} tMax={tMax} />
      <MiniStrip data={curve} dataKey="drum" color={C_DRM} label="Drum" unit="Hz" domain={["dataMin - 2", "dataMax + 2"]} tMax={tMax} />

      <div className="h-64 sm:h-80 mt-2">
        <ResponsiveContainer>
          <ComposedChart data={curve} margin={{ top: 8, right: 30, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(25 10% 65%)" strokeOpacity={0.7} />
            {/* Belt-and-braces horizontal gridlines — recharts' CartesianGrid sometimes
                skips horizontals when there are multiple Y axes with allowDataOverflow.
                ReferenceLines guarantee the temp ticks are visible. */}
            {[80, 100, 120, 140, 160, 180, 200, 220, 240].map((y) => (
              <ReferenceLine key={`g-${y}`} yAxisId="temp" y={y}
                stroke="hsl(25 10% 65%)" strokeOpacity={0.5} strokeDasharray="3 3" />
            ))}
            <XAxis
              dataKey="t" type="number" domain={[0, tMax || "dataMax"]}
              tickFormatter={(t) => mmss(t)}
              tick={{ fontSize: 10, fill: "hsl(25 8% 42%)" }}
              ticks={Array.from({ length: Math.max(2, Math.ceil((tMax || 0) / 60) + 1) }, (_, i) => i * 60)}
            />
            <YAxis
              yAxisId="temp" width={36}
              tick={{ fontSize: 10, fill: "hsl(25 8% 42%)" }}
              domain={[80, "dataMax + 20"]}
              ticks={[80, 100, 120, 140, 160, 180, 200, 220, 240]}
              allowDataOverflow
            />
            <YAxis
              yAxisId="ror" orientation="right" width={32}
              tick={{ fontSize: 10, fill: "hsl(25 8% 42%)" }}
              domain={[-4, 20]}
              ticks={[-4, 0, 4, 8, 12, 16, 20]}
              allowDataOverflow
            />
            <Tooltip
              contentStyle={{ background: "white", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 11 }}
              labelFormatter={(t) => `t = ${mmss(t)}`}
              formatter={(v, n) => [v != null ? Number(v).toFixed(1) : "—", n]}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="plainline" />
            {firstCrack > 0 && (
              <ReferenceLine yAxisId="temp" x={firstCrack} stroke={C_FC} strokeWidth={2}
                strokeDasharray="4 3"
                label={{
                  value: `FC ${mmss(firstCrack)}`, fill: C_FC, fontSize: 11,
                  position: "top", fontWeight: 600,
                }} />
            )}
            <Line yAxisId="temp" dataKey="bt"  name="BT (°C)"          stroke={C_BT}     strokeWidth={2}   dot={false} isAnimationActive={false} />
            <Line yAxisId="temp" dataKey="et"  name="ET (°C)"          stroke={C_ET}     strokeWidth={2}   dot={false} isAnimationActive={false} />
            <Line yAxisId="ror"  dataKey="ror"    name="BT RoR (°/min)" stroke={C_BT_ROR} strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls />
            <Line yAxisId="ror"  dataKey="et_ror" name="ET RoR (°/min)" stroke={C_ET_ROR} strokeWidth={1.4} strokeDasharray="5 3" dot={false} isAnimationActive={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
